import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  BarChart3
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      title: "Smart Invoicing",
      description: "Auto-calculate GST, fuel surcharges, and docket fees instantly. Generate professional PDFs in one click.",
      icon: FileText
    },
    {
      title: "Client Management",
      description: "Keep detailed customer profiles, track billing history, and manage franchise agreements seamlessly.",
      icon: Users
    },
    {
      title: "Financial Analytics",
      description: "Visual dashboards for revenue, pending payments, and growth trends. Know where your business stands.",
      icon: TrendingUp
    },
    {
      title: "Data Security",
      description: "Enterprise-grade security with role-based access control. Your franchise data is safe and encrypted.",
      icon: Shield
    },
    {
      title: "Fast Export",
      description: "Export reports to Excel or PDF for offline analysis and auditing. Data portability made simple.",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 overflow-x-hidden">

      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-indigo-950/90 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow-inner">
              FR
            </div>
            <span className="text-xl font-bold tracking-tight text-white">
              BillNet
            </span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Platform</a>
            <a href="#" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Solutions</a>
            <a href="#" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Resources</a>
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white border border-white/20 hover:bg-white/10 transition-all"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-md transform hover:-translate-y-0.5"
            >
              Get Started
            </button>
          </div>

          <div className="md:hidden">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-indigo-950 pt-24 px-6 md:hidden text-white">
          <div className="flex flex-col space-y-6 text-xl font-medium">
            <a onClick={() => setMobileMenuOpen(false)} href="#features">Platform</a>
            <a onClick={() => setMobileMenuOpen(false)} href="#">Solutions</a>
            <a onClick={() => setMobileMenuOpen(false)} href="#">Resources</a>
            <button
              onClick={() => navigate('/login')}
              className="bg-indigo-600 text-white py-3 rounded-lg w-full"
            >
              Log In
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Split Layout */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-indigo-950 text-white">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left Content */}
            <div className="lg:w-1/2 text-left">
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
                Effortless Invoicing. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">
                  Powerful Control.
                </span>
              </h1>

              <p className="text-lg text-indigo-200 mb-8 max-w-xl leading-relaxed">
                Streamline your franchise operations with a platform designed for speed and accuracy.
                Manage billing, compliance, and growth—all from a single, intuitive dashboard.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold text-lg transition-all shadow-lg shadow-indigo-900/50 flex items-center justify-center gap-2"
                >
                  Start Now <ArrowRight className="h-5 w-5" />
                </button>
                <button className="px-8 py-4 bg-transparent border border-white/20 hover:bg-white/5 text-white rounded-lg font-semibold text-lg transition-all">
                  Contact Sales
                </button>
              </div>

              <div className="flex items-center gap-6 text-sm text-indigo-300 font-medium">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>GST Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>Secure Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>

            {/* Right Visual (Abstract Dashboard Representation) */}
            <div className="lg:w-1/2 relative">
              <div className="relative rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 shadow-2xl skew-y-3 lg:skew-y-0 lg:rotate-2 transform transition-transform hover:scale-[1.02] duration-500">
                {/* Mock UI Elements */}
                <div className="absolute -top-4 -right-4 h-24 w-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-2xl opacity-40"></div>
                <div className="absolute -bottom-8 -left-8 h-32 w-32 bg-cyan-500 rounded-full blur-3xl opacity-20"></div>

                <div className="flex items-center gap-4 mb-8">
                  <div className="h-3 w-3 rounded-full bg-red-400"></div>
                  <div className="h-3 w-3 rounded-full bg-yellow-400"></div>
                  <div className="h-3 w-3 rounded-full bg-green-400"></div>
                  <div className="ml-auto px-3 py-1 bg-white/10 rounded-full text-xs text-indigo-200">Dashboard View</div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                    <div className="text-zinc-400 text-xs mb-1">Total Revenue</div>
                    <div className="text-2xl font-bold text-white">₹2.4M</div>
                    <div className="text-green-400 text-xs mt-1 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> +12.5%</div>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                    <div className="text-zinc-400 text-xs mb-1">Active Clients</div>
                    <div className="text-2xl font-bold text-white">84</div>
                    <div className="text-indigo-400 text-xs mt-1">Active now</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="h-10 rounded bg-white/5 w-full"></div>
                  <div className="h-10 rounded bg-white/5 w-3/4"></div>
                  <div className="h-10 rounded bg-white/5 w-5/6"></div>
                  <div className="h-20 rounded bg-gradient-to-r from-indigo-600/50 to-purple-600/50 w-full mt-4 flex items-center justify-center text-indigo-100 text-sm font-medium">
                    <BarChart3 className="mr-2 h-4 w-4" /> Real-time Analytics
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid - Clean Enterprise Look */}
      <section id="features" className="py-24 bg-white relative">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <span className="text-indigo-600 font-semibold tracking-wider uppercase text-sm">Enterprise Features</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3 mb-6">Designed for Modern Franchises</h2>
            <p className="text-slate-600 text-lg">Scalable, secure, and built to handle your complex billing needs with ease.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-12">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className="h-12 w-12 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm lg:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Transform your workflow today.</h2>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-3 bg-indigo-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-slate-100">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-indigo-600 rounded flex items-center justify-center text-white font-bold text-xs">
                FR
              </div>
              <span className="text-lg font-bold text-slate-900">BillNet</span>
            </div>

            <div className="text-slate-500 text-sm">
              &copy; 2025 FR-BillNet. All rights reserved.
            </div>

            <div className="flex items-center text-sm text-slate-400">
              Powered by SoftNex
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Home;
